import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ArrowRight, Check, CircleNotch, CheckSquare, Square } from "@phosphor-icons/react";
import { toast } from "sonner";

const DiagnosticModal = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    systems: [],
  });

  const systemCategories = [
    {
      category: "ERP & Finance",
      systems: ["SAP", "Oracle", "Microsoft Dynamics 365", "NetSuite", "Tally", "Zoho Books", "Other"]
    },
    {
      category: "CRM & Sales",
      systems: ["Salesforce", "HubSpot", "Zoho CRM", "Freshsales", "LeadSquared", "Other"]
    },
    {
      category: "HR & Payroll",
      systems: ["Workday", "Darwinbox", "Zoho People", "Freshteam", "SAP SuccessFactors", "Other"]
    },
    {
      category: "Service & Support",
      systems: ["ServiceNow", "Freshdesk", "Zendesk", "Jira Service Management", "Other"]
    },
    {
      category: "Collaboration & Productivity",
      systems: ["Google Workspace", "Microsoft 365", "Slack", "Atlassian (Jira/Confluence)", "Other"]
    },
    {
      category: "Payments & Banking",
      systems: ["Razorpay", "PayU", "HDFC SmartHub", "ICICI Eazypay", "Other"]
    },
    {
      category: "Industry-Specific",
      systems: ["Shopify", "Procore", "Epic", "Temenos", "Blue Yonder", "Manhattan", "Other"]
    }
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleSystem = (system) => {
    setFormData(prev => ({
      ...prev,
      systems: prev.systems.includes(system)
        ? prev.systems.filter(s => s !== system)
        : [...prev.systems, system]
    }));
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name || !formData.email || !formData.company) {
        toast.error("Please fill in all required fields");
        return;
      }
      setStep(2);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setLoading(false);
    setStep(3);
    toast.success("Diagnostic request submitted successfully!");
  };

  const handleClose = () => {
    setStep(1);
    setFormData({ name: "", email: "", company: "", role: "", systems: [] });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="sm:max-w-lg bg-[#0F0F10] border border-white/10 text-white max-h-[90vh] overflow-y-auto"
        data-testid="diagnostic-modal"
        data-lenis-prevent
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-['Outfit'] text-white">
            {step === 3 ? "Request Received" : "Start Your AI Diagnostic"}
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            {step === 1 && "Tell us about yourself and your organization."}
            {step === 2 && "Select the systems your organization uses."}
            {step === 3 && "We'll reach out shortly to begin your diagnostic journey."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#FAFAFA]">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="John Smith"
                className="form-input"
                data-testid="diagnostic-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#FAFAFA]">Work Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john@company.com"
                className="form-input"
                data-testid="diagnostic-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company" className="text-[#FAFAFA]">Company *</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Acme Corp"
                className="form-input"
                data-testid="diagnostic-company-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role" className="text-[#FAFAFA]">Your Role</Label>
              <Input
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                placeholder="CEO, CFO, CTO, etc."
                className="form-input"
                data-testid="diagnostic-role-input"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label className="text-[#FAFAFA]">
                What systems does your organization use?
              </Label>
              <p className="text-xs text-[#71717A]">
                Select all that apply. This helps us tailor the diagnostic.
              </p>
            </div>
            
            <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2">
              {systemCategories.map((cat) => (
                <div key={cat.category} className="space-y-2">
                  <span className="text-xs text-[#00E5FF] uppercase tracking-wider font-medium">
                    {cat.category}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {cat.systems.map((system) => {
                      const isSelected = formData.systems.includes(system);
                      return (
                        <button
                          key={system}
                          type="button"
                          onClick={() => toggleSystem(system)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
                            isSelected
                              ? "bg-[#00E5FF]/20 border border-[#00E5FF]/50 text-[#00E5FF]"
                              : "bg-white/5 border border-white/10 text-[#A1A1AA] hover:border-white/20 hover:text-white"
                          }`}
                          data-testid={`system-${system.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {isSelected ? (
                            <CheckSquare size={14} weight="fill" />
                          ) : (
                            <Square size={14} />
                          )}
                          {system}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {formData.systems.length > 0 && (
              <div className="pt-2 border-t border-white/10">
                <p className="text-xs text-[#71717A]">
                  Selected: <span className="text-[#00E5FF]">{formData.systems.length} systems</span>
                </p>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00E5FF]/20 flex items-center justify-center">
              <Check size={32} className="text-[#00E5FF]" />
            </div>
            <p className="text-[#A1A1AA] text-sm">
              Thank you, {formData.name}. Our team will contact you at {formData.email} within 24 hours 
              to schedule your enterprise AI diagnostic.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
          {step < 3 && (
            <>
              <Button
                variant="ghost"
                onClick={handleClose}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                data-testid="diagnostic-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                disabled={loading}
                className="btn-primary rounded-full px-6 flex items-center gap-2"
                data-testid="diagnostic-next-btn"
              >
                {loading ? (
                  <CircleNotch size={18} className="animate-spin" />
                ) : (
                  <>
                    {step === 1 ? "Next" : "Submit"}
                    <ArrowRight size={16} />
                  </>
                )}
              </Button>
            </>
          )}
          {step === 3 && (
            <Button
              onClick={handleClose}
              className="btn-primary rounded-full px-6"
              data-testid="diagnostic-done-btn"
            >
              Done
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DiagnosticModal;
